import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Users, UserCheck, UserX, Shield, Mail, Calendar,
  Search, Trash2, CheckCircle, XCircle, Loader, TrendingUp
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const AdminDashboard = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');

  // Redirect non-admin users
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to access the admin dashboard.",
        variant: "destructive"
      });
      navigate('/');
    }
  }, [authLoading, isAuthenticated, user, navigate, toast]);

  // Fetch users and stats
  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user, searchTerm, filterProvider, filterVerified]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterProvider !== 'all') params.append('auth_provider', filterProvider);
      if (filterVerified !== 'all') params.append('is_verified', filterVerified === 'verified');

      // Fetch users, stats, and subscriptions in parallel
      const [usersResponse, statsResponse, subscriptionsResponse] = await Promise.all([
        apiClient.get(`/admin/users?${params.toString()}`),
        apiClient.get(`/admin/stats`),
        apiClient.get(`/subscription/admin/all-subscriptions`)
      ]);

      setUsers(usersResponse.data);
      setStats(statsResponse.data);
      setSubscriptions(subscriptionsResponse.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast({
        title: "Error",
        description: "Failed to load admin data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const removeRecurringDiscount = async (userId, userName) => {
    if (!window.confirm(`Remove student discount from "${userName}"? They will be charged full price (£9.99) on next billing cycle.`)) {
      return;
    }

    try {
      const response = await apiClient.put(`/subscription/admin/remove-discount/${userId}`);

      toast({
        title: "Success",
        description: response.data.message,
      });

      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to remove discount",
        variant: "destructive"
      });
    }
  };

  const toggleAdminStatus = async (userId, currentStatus) => {
    try {
      await apiClient.put(`/admin/users/${userId}/admin`, null, {
        params: { make_admin: !currentStatus }
      });

      toast({
        title: "Success",
        description: `Admin status ${!currentStatus ? 'granted' : 'revoked'}`,
      });

      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update admin status",
        variant: "destructive"
      });
    }
  };

  const verifyUserEmail = async (userId) => {
    try {
      await apiClient.put(`/admin/users/${userId}/verify`);

      toast({
        title: "Success",
        description: "User email verified successfully",
      });

      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to verify email",
        variant: "destructive"
      });
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiClient.delete(`/admin/users/${userId}`);

      toast({
        title: "Success",
        description: "User deleted successfully",
      });

      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete user",
        variant: "destructive"
      });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-red-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 font-heading mb-2">
            Admin Dashboard
          </h1>
          <p className="text-gray-600">Manage users and view platform statistics</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  Total Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{stats.total_users}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                  <UserCheck className="w-4 h-4 mr-2" />
                  Verified Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.verified_users}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.unverified_users} unverified
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                  <Shield className="w-4 h-4 mr-2" />
                  Admin Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.admin_users}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  New (7 days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.recent_users}</div>
                <div className="flex gap-2 mt-2 text-xs">
                  <Badge variant="outline" className="text-xs">
                    📧 {stats.email_users}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    🔍 {stats.google_users}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">User Management</CardTitle>
            <CardDescription>Search, filter, and manage user accounts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <select
                value={filterProvider}
                onChange={(e) => setFilterProvider(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Providers</option>
                <option value="email">Email</option>
                <option value="google">Google</option>
              </select>

              <select
                value={filterVerified}
                onChange={(e) => setFilterVerified(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Status</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Provider
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscription
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-green-500 to-red-500 flex items-center justify-center text-white font-semibold">
                              {u.name.charAt(0).toUpperCase()}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                              {u.name}
                              {u.is_admin && (
                                <Badge variant="destructive" className="text-xs">
                                  <Shield className="w-3 h-3 mr-1" />
                                  Admin
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="outline" className="capitalize">
                          {u.auth_provider === 'google' ? '🔍 Google' : '📧 Email'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {u.is_verified ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                            <XCircle className="w-3 h-3 mr-1" />
                            Unverified
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {subscriptions[u.id]?.is_subscribed ? (
                          <div className="space-y-1">
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              ✓ Active
                            </Badge>
                            {subscriptions[u.id]?.subscription_type === 'promo_recurring' && (
                              <div className="text-xs text-orange-600 font-medium">
                                🎓 50% Student Discount
                              </div>
                            )}
                            {subscriptions[u.id]?.subscription_type === 'lifetime_free' && (
                              <div className="text-xs text-purple-600 font-medium">
                                🎉 Lifetime Free
                              </div>
                            )}
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">
                            None
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {new Date(u.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-col gap-1">
                          {!u.is_verified && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => verifyUserEmail(u.id)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50 justify-start"
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Verify
                            </Button>
                          )}
                          
                          {subscriptions[u.id]?.subscription_type === 'promo_recurring' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeRecurringDiscount(u.id, u.name)}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 justify-start"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Remove Discount
                            </Button>
                          )}
                          
                          {u.id !== user?.id && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => toggleAdminStatus(u.id, u.is_admin)}
                                className={`justify-start ${u.is_admin ? "text-red-600 hover:text-red-700 hover:bg-red-50" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"}`}
                              >
                                <Shield className="w-4 h-4 mr-1" />
                                {u.is_admin ? 'Revoke Admin' : 'Make Admin'}
                              </Button>
                              
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteUser(u.id, u.name)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 justify-start"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete User
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {users.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No users found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
